import { jwtDecode } from 'jwt-decode';

import { Routes, urls } from '@/constants';
import { PostgrestEndpoints } from '@/requests';
import { AnalyticsService, EVENTS } from '@/services';
import {
  camelCaseKeys,
  getAccessToken,
  getRefreshToken,
  removeAccessToken,
  removeRefreshToken,
  removeUtmParametersCookie,
} from '@/utils';

import { postgrestApi } from '../postgrest-api';
import { setTokens, Token } from '../session';
import {
  handleOnQueryStartedError,
  rtkQueryError,
  transformAuthErrorResponse,
  transformErrorResponse,
} from '../store.utils';
import {
  ForgotPasswordErrorCodes,
  RegistrationResponseCodes,
} from './auth-api.constants';
import {
  LoginPayload,
  LoginResponse,
  RegisterPayload,
  RegisterResponse,
  ResetPasswordWithTokenPayload,
  SendEpisodeShareEmailPayload,
  SendFreeTrialShareEmailPayload,
  SendResetPasswordEmailPayload,
  ValidatePasswordResetTokenPayload,
} from './auth-api.types';

export const authApi = postgrestApi.injectEndpoints({
  endpoints: builder => ({
    login: builder.mutation<LoginResponse, LoginPayload>({
      query: ({ email, password }) => ({
        url: PostgrestEndpoints.LOGIN,
        method: 'POST',
        body: {
          email,
          password,
        },
      }),
      onQueryStarted: async ({ referrer }, { dispatch, queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.LOGIN.INTENT, {
            location: referrer ?? '',
          });
          const {
            data: { accessToken, refreshToken },
          } = await queryFulfilled;

          AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.LOGIN.SUCCESS, {
            location: referrer ?? '',
          });
          AnalyticsService.logEvent(EVENTS.GA4_KEY_EVENTS.LOGIN);
          await dispatch(setTokens({ accessToken, refreshToken }));
        } catch (error: any) {
          const payload = await error?.meta?.request?.json?.();
          AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.LOGIN.FAIL, {
            email: payload?.email,
            reason: error?.error?.data?.message,
          });
          handleOnQueryStartedError(error);
        }
      },
      transformResponse: (res: any) => {
        return {
          accessToken: res.access_token,
          refreshToken: res.refresh_token,
          userId: res.user.user_id,
        };
      },
    }),
    sendResetPasswordEmail: builder.mutation<
      void,
      SendResetPasswordEmailPayload
    >({
      queryFn: async ({ email }, redux, extraOptions, baseQuery) => {
        const { error } = await baseQuery({
          url: PostgrestEndpoints.FORGOT_PASSWORD,
          method: 'POST',
          body: { email },
        });

        // Swallow unknown email error(s) to prevent exposing it to the user
        if (
          error &&
          ![
            'lumiq.account.reset_password.unknown_email',
            'lumiq.account_recovery.recovery_account.recovery_email_not_associated_with_any_non_removed_user',
          ].includes((error.data as any)?.details)
        ) {
          return {
            error: transformErrorResponse(error, ForgotPasswordErrorCodes),
          };
        }

        return { data: undefined };
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(
            EVENTS.AUTH_EVENTS.SEND_PASSWORD_RESET_EMAIL.INTENT,
          );
          await queryFulfilled;
          AnalyticsService.logEvent(
            EVENTS.AUTH_EVENTS.SEND_PASSWORD_RESET_EMAIL.SUCCESS,
          );
        } catch (error: any) {
          AnalyticsService.logEvent(
            EVENTS.AUTH_EVENTS.SEND_PASSWORD_RESET_EMAIL.FAIL,
            {
              reason: error?.error?.data?.message,
            },
          );
          handleOnQueryStartedError(error);
        }
      },
    }),
    validatePasswordResetToken: builder.query<
      boolean,
      ValidatePasswordResetTokenPayload
    >({
      query: ({ token }) => ({
        url: PostgrestEndpoints.VALIDATE_PASSWORD_RESET_TOKEN,
        method: 'POST',
        body: { _password_reset_token: token },
      }),
    }),
    resetPasswordWithToken: builder.mutation<
      boolean,
      ResetPasswordWithTokenPayload
    >({
      query: ({ password, token }) => ({
        url: PostgrestEndpoints.RESET_PASSWORD_WITH_TOKEN,
        method: 'POST',
        body: {
          _password_reset_token: token,
          _password: password,
        },
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.PASSWORD_RESET.INTENT);
          await queryFulfilled;
          AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.PASSWORD_RESET.SUCCESS);
        } catch (error: any) {
          AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.PASSWORD_RESET.FAIL, {
            reason: error?.error?.data?.message,
          });
          handleOnQueryStartedError(error);
        }
      },
    }),
    logout: builder.mutation<boolean, void>({
      queryFn: async (_, __, ___, baseQuery) => {
        try {
          const accessToken = getAccessToken();
          const refreshToken = getRefreshToken();

          if (accessToken && refreshToken) {
            const userId = jwtDecode<Token>(accessToken)?.user?.id;

            const { error } = await baseQuery({
              url: PostgrestEndpoints.LOGOUT,
              method: 'POST',
              body: {
                refresh_token: refreshToken,
                user_id: userId,
              },
            });

            AnalyticsService.reset();
            removeUtmParametersCookie();
            removeAccessToken();
            removeRefreshToken();

            const targetUrl = `${urls.appBase}${Routes.DISCOVER}`;
            window.location.replace(targetUrl);

            if (error) return { error };
          }

          return { data: true };
        } catch (error) {
          return rtkQueryError(error);
        }
      },
    }),
    register: builder.mutation<RegisterResponse, RegisterPayload>({
      query: ({
        email,
        password,
        marketingOptIn,
        offerId,
        inviteToken,
        utmParams,
      }) => ({
        url: PostgrestEndpoints.REGISTER,
        method: 'POST',
        body: {
          email,
          password,
          args: {
            ...utmParams,
            ...(offerId ? { offer_id: offerId } : {}),
            ...(inviteToken ? { invite_token: inviteToken } : {}),
            marketing_opt_in: marketingOptIn,
          },
        },
      }),
      onQueryStarted: async ({ referrer }, { dispatch, queryFulfilled }) => {
        try {
          AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.REGISTER.INTENT, {
            location: referrer ?? '',
          });
          const {
            data: { accessToken, refreshToken },
          } = await queryFulfilled;

          AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.REGISTER.SUCCESS, {
            location: referrer ?? '',
            method: 'email and password',
          });

          if (accessToken && refreshToken) {
            const userId = jwtDecode<Token>(accessToken)?.user?.id;
            AnalyticsService.identify({ userId });
            dispatch(setTokens({ accessToken, refreshToken }));
          }
        } catch (error: any) {
          const payload = await error?.meta?.request?.json?.();
          AnalyticsService.logEvent(EVENTS.AUTH_EVENTS.REGISTER.FAIL, {
            email: payload?.email,
            reason: error?.error?.data?.message,
          });
          handleOnQueryStartedError(error);
        }
      },
      transformResponse: (res: any) => {
        const { response } = res as {
          response: {
            access_token?: string;
            refresh_token?: string;
          };
        };
        return {
          accessToken: response?.access_token,
          refreshToken: response?.refresh_token,
        };
      },
      transformErrorResponse: (error: any) => {
        return transformAuthErrorResponse(error, RegistrationResponseCodes);
      },
    }),
    validateInviteToken: builder.query<boolean, { inviteToken: string }>({
      query: ({ inviteToken }) => ({
        url: PostgrestEndpoints.VALIDATE_INVITE_TOKEN,
        method: 'POST',
        body: { invite_token: inviteToken },
      }),
    }),
    getInviteDetails: builder.query<
      { organizationName: string; claimedByUserId: string; email: string },
      { inviteToken: string }
    >({
      query: ({ inviteToken }) => ({
        url: PostgrestEndpoints.GET_INVITE_DETAILS,
        method: 'POST',
        body: { invite_token: inviteToken },
      }),
      transformResponse: (response: any) => camelCaseKeys(response),
    }),
    getSSORedirectUri: builder.query<
      string | null,
      { email: string; redirect?: string }
    >({
      query: ({ email, redirect }) => ({
        url: PostgrestEndpoints.GET_SSO_REDIRECT_URI,
        method: 'POST',
        body: {
          email,
          redirect_uri: urls.auth.workos,
          state: btoa(
            JSON.stringify({
              redirect:
                redirect || `${window.location.origin}${Routes.DISCOVER}`,
            }),
          ),
        },
      }),
      transformResponse: (response: any) => response.url,
    }),
    genAppLoginMagicLink: builder.query<
      { magicLinkToken: string; expiresAt: string },
      void
    >({
      query: () => ({
        url: PostgrestEndpoints.GEN_APP_LOGIN_MAGIC_LINK,
        method: 'POST',
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        const { INTENT, SUCCESS, FAIL } =
          EVENTS.APP_LOGIN_MAGIC_LINK_EVENTS.GENERATE;

        try {
          AnalyticsService.logEvent(INTENT);
          await queryFulfilled;
          AnalyticsService.logEvent(SUCCESS);
        } catch (error: any) {
          const reason = error?.error?.data?.message;
          AnalyticsService.logEvent(FAIL, { reason });
          handleOnQueryStartedError(error);
        }
      },
      keepUnusedDataFor: 0,
      transformResponse: (response: any) => camelCaseKeys(response),
    }),
    verifyEmail: builder.mutation<
      {
        response: {
          accessToken: string;
          refreshToken: string;
          magicLinkToken: string;
        };
      },
      { token: string }
    >({
      query: ({ token }) => ({
        url: PostgrestEndpoints.VERIFY_EMAIL,
        method: 'POST',
        body: { verification_code: token },
      }),
      transformResponse: (response: any) => camelCaseKeys(response),
    }),
    resendSSOConfirmationEmail: builder.mutation<
      void,
      { email: string; providerId: string; provider: string }
    >({
      query: ({ email, providerId, provider }) => ({
        url: PostgrestEndpoints.RESEND_CONFIRMATION_EMAIL_OAUTH,
        method: 'POST',
        body: { provider_email: email, provider_id: providerId, provider },
      }),
    }),
    sendFreeTrialShareEmail: builder.mutation<
      void,
      SendFreeTrialShareEmailPayload
    >({
      query: ({ firstName, email, message, compliance, queryParams }) => ({
        url: PostgrestEndpoints.SEND_FREE_TRIAL_SHARE_EMAIL,
        method: 'POST',
        body: {
          recipient_first_name: firstName,
          recipient_email: email,
          message: message ?? '',
          compliance,
          passed_through_query_params: queryParams,
        },
      }),
      onQueryStarted: async (payload, { queryFulfilled }) => {
        const { INTENT, SUCCESS, FAIL } = EVENTS.FREE_TRIAL_SHARE_EVENTS.SHARE;

        try {
          AnalyticsService.logEvent(INTENT, {
            recipientEmail: payload.email,
            recipientFirstName: payload.firstName,
            recipientLastName: payload.lastName,
            shareMessage: payload.message ?? '',
          });
          await queryFulfilled;
          AnalyticsService.logEvent(SUCCESS);
        } catch (error: any) {
          const reason = error?.error?.data?.message;
          AnalyticsService.logEvent(FAIL, { reason });
          handleOnQueryStartedError(error);
        }
      },
    }),
    sendEpisodeShareEmail: builder.mutation<void, SendEpisodeShareEmailPayload>(
      {
        query: ({ firstName, email, episodeId, compliance }) => ({
          url: PostgrestEndpoints.SEND_EPISODE_SHARE_EMAIL,
          method: 'POST',
          body: {
            recipient_first_name: firstName,
            recipient_email: email,
            episode_id: episodeId,
            compliance,
          },
        }),
        onQueryStarted: async (payload, { queryFulfilled }) => {
          const { INTENT, SUCCESS, FAIL } = EVENTS.EPISODE_SHARE_EVENTS.EMAIL;

          try {
            AnalyticsService.logEvent(INTENT, {
              recipientEmail: payload.email,
              recipientFirstName: payload.firstName,
              recipientLastName: payload.lastName,
              episodeId: payload.episodeId,
            });
            await queryFulfilled;
            AnalyticsService.logEvent(SUCCESS);
          } catch (error: any) {
            const reason = error?.error?.data?.message;
            AnalyticsService.logEvent(FAIL, { reason });
            handleOnQueryStartedError(error);
          }
        },
      },
    ),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useSendResetPasswordEmailMutation,
  useValidatePasswordResetTokenQuery,
  useResetPasswordWithTokenMutation,
  useRegisterMutation,
  useValidateInviteTokenQuery,
  useGetInviteDetailsQuery,
  useGetSSORedirectUriQuery,
  useLazyGetSSORedirectUriQuery,
  useVerifyEmailMutation,
  useResendSSOConfirmationEmailMutation,
  useGenAppLoginMagicLinkQuery,
  useSendFreeTrialShareEmailMutation,
  useSendEpisodeShareEmailMutation,
} = authApi;
